import { OntologyIcon, ProjectIcon } from '@prophecy/icons';
import { ICON_CLASS } from '@prophecy/icons/tokens';
import { BaseColor, theme } from '@prophecy/theme';
import { Dialog, FeaturedIcon, Input, Stack, TextArea } from '@prophecy/ui-v3';
import utilify from '@prophecy/utils/react/utilify';
import { validateSQLProjectName } from '@prophecy/utils/validation';
import { ReactNode, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

const StyledButtonContainer = styled(Stack)`
  padding-top: ${theme.spaces.x12};
  flex-direction: row;
  button:first-child {
    order: 1;
  }
`;

const ButtonsContainer = ({ children }: { children: ReactNode }) => (
  <StyledButtonContainer gap={theme.spaces.x12}>{children}</StyledButtonContainer>
);

export type ProjectDetails = { name: string; description: string };

export type ProjectDialogKind = 'project' | 'types';

type DialogContent = {
  icon: ReactNode;
  iconColor: string;
  title: string;
  subTitle: string;
  submitLabel: string;
  namePlaceholder: string;
  descriptionPlaceholder: string;
  testIdPrefix: string;
};

const DIALOG_CONTENT: Record<ProjectDialogKind, DialogContent> = {
  project: {
    icon: <ProjectIcon type='extended' />,
    iconColor: theme.colors.brand600,
    title: 'Create a Project',
    subTitle: 'Projects hold the pipelines and data your team builds.',
    submitLabel: 'Create Project',
    namePlaceholder: 'e.g. loan_tape_cleanup',
    descriptionPlaceholder: 'e.g. Cleans and standardises monthly loan tapes.',
    testIdPrefix: 'create-project'
  },
  types: {
    icon: <OntologyIcon type='extended' />,
    iconColor: theme.colors.pink600,
    title: 'Create a Types Project',
    subTitle: 'Types describe your business concepts, so your projects can share them.',
    submitLabel: 'Create Types Project',
    namePlaceholder: 'e.g. us_auto_abs',
    descriptionPlaceholder: 'e.g. Shared types for structured finance: Loan, Deal, Tranche.',
    testIdPrefix: 'create-types-project'
  }
};

type CreateProjectDialogProps = {
  kind: ProjectDialogKind;
  open: boolean;
  onClose: () => void;
  onCreate: (details: ProjectDetails) => Promise<unknown> | void;
  defaultName: string;
  existingProjectNames: string[];
};

function createNameValidator(existingProjectNames: string[]) {
  const existing = new Set(existingProjectNames.map((name) => name.toLowerCase()));
  return (name: string) => {
    const formatError = validateSQLProjectName(name);
    if (formatError) return formatError;
    return existing.has(name.toLowerCase()) ? `Project "${name}" already exists` : undefined;
  };
}

const DialogTitle = styled(Stack)<{ $iconColor: string }>`
  align-items: flex-start;

  .${ICON_CLASS}.${ICON_CLASS} {
    color: ${({ $iconColor }) => $iconColor};
  }
`;

export function CreateProjectDialog({
  kind,
  open,
  onClose,
  onCreate,
  defaultName,
  existingProjectNames
}: CreateProjectDialogProps) {
  const content = DIALOG_CONTENT[kind];
  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<ProjectDetails>({ mode: 'onChange', defaultValues: { name: defaultName, description: '' } });

  const [isCreating, setIsCreating] = useState(false);

  const validateName = useMemo(() => createNameValidator(existingProjectNames), [existingProjectNames]);

  const handleCreate = handleSubmit(async (details) => {
    setIsCreating(true);
    try {
      await onCreate({ name: details.name.trim(), description: details.description.trim() });
    } finally {
      setIsCreating(false);
    }
  });

  return (
    <Dialog
      open={open}
      size='l'
      title={
        <DialogTitle gap={theme.spaces.x16} $iconColor={content.iconColor}>
          <FeaturedIcon variant='modern' tone={BaseColor.gray} icon={content.icon} />
          {content.title}
        </DialogTitle>
      }
      subTitle={content.subTitle}
      okButton={{
        children: content.submitLabel,
        disabled: !isValid,
        loading: isCreating,
        size: 'l',
        block: true,
        'data-test-id': `${content.testIdPrefix}-submit`
      }}
      closeButton={{
        children: 'Cancel',
        disabled: isCreating,
        size: 'l',
        block: true,
        'data-test-id': `${content.testIdPrefix}-cancel`
      }}
      closable={!isCreating}
      onOk={handleCreate}
      onClose={onClose}
      ButtonsContainer={ButtonsContainer}
      data-test-id={`${content.testIdPrefix}-dialog`}>
      <Stack gap={theme.spaces.x16} padding={`${theme.spaces.x20} 0 0`}>
        <Controller
          control={control}
          name='name'
          rules={{ required: 'Name is required', validate: validateName }}
          render={({ field }) => (
            <Input
              {...field}
              autoFocus
              label='Name'
              placeholder={content.namePlaceholder}
              required
              variant={errors.name ? 'error' : 'primary'}
              helpText={errors.name?.message}
              data-test-id={`${content.testIdPrefix}-name-input`}
            />
          )}
        />
        <Controller
          control={control}
          name='description'
          render={({ field }) => (
            <TextArea
              {...field}
              label='Description'
              placeholder={content.descriptionPlaceholder}
              data-test-id={`${content.testIdPrefix}-description-input`}
            />
          )}
        />
      </Stack>
    </Dialog>
  );
}

type OpenCreateProjectDialogProps = Omit<CreateProjectDialogProps, 'open' | 'onClose' | 'onCreate'> & {
  createProject: (details: ProjectDetails) => Promise<string | undefined>;
};

export const openCreateProjectDialog = utilify<OpenCreateProjectDialogProps, Promise<string | null>>(
  ({ props: { createProject, ...props }, render, unmount }) => {
    return new Promise((resolve) => {
      const handleClose = () => {
        resolve(null);
        unmount();
      };
      const handleCreate = async (details: ProjectDetails) => {
        const projectId = await createProject(details);
        if (!projectId) return;
        resolve(projectId);
        unmount();
      };
      render(<CreateProjectDialog {...props} open onClose={handleClose} onCreate={handleCreate} />, {
        unmountOnNavigation: true
      });
    });
  }
);
